import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { resolveLangKey } from "../lib/resolve-lang";
import { setLocale } from "../locale";

/**
 * Per-visitor content language for the published site.
 *
 * Resolves the visitor's language against the site's available languages
 * ([primaryLanguage, ...languages]) using the tolerant resolveLangKey chain
 * (exact → base → region-collapse → primary). Detection order:
 *   ?lang= query  >  stored choice (localStorage)  >  guest language  >  navigator
 * The switcher overrides detection and persists the choice.
 *
 * This is the runtime twin of the editor's A4 currentLanguage: leaf components
 * call useWBLang() and resolve their per-language text props with it.
 */
type WBLangContextType = {
  currentLang: string;
  primaryLanguage: string;
  availableLanguages: string[];
  setCurrentLang: (lang: string) => void;
};

const WBLangContext = createContext<WBLangContextType | undefined>(undefined);

const STORAGE_KEY = "wb-lang";

function readQueryLang(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const v = new URLSearchParams(window.location.search).get("lang");
  return v && v.length > 0 ? v : undefined;
}

function readStoredLang(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function detectNavigatorLang(): string | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (
    navigator.language ??
    (navigator.languages?.[0] as string | undefined) ??
    undefined
  );
}

export const WBLanguageRuntimeProvider = ({
  primaryLanguage,
  languages,
  guestLanguage,
  children,
}: {
  primaryLanguage: string | undefined;
  languages: string[] | undefined;
  guestLanguage?: string | undefined;
  children: ReactNode;
}) => {
  const primary = primaryLanguage && primaryLanguage.length > 0 ? primaryLanguage : "de-DE";
  const availableLanguages = useMemo(
    () => Array.from(new Set([primary, ...(languages ?? [])])),
    [primary, languages],
  );

  const [currentLang, setCurrentLangState] = useState<string>(() => {
    const detected =
      readQueryLang() ?? readStoredLang() ?? guestLanguage ?? detectNavigatorLang();
    return resolveLangKey(availableLanguages, detected, primary);
  });

  // Keep the UI-string locale + <html lang> in sync with the content language.
  useEffect(() => {
    setLocale(currentLang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = currentLang;
    }
  }, [currentLang]);

  // Emit <link rel="alternate" hreflang="…"> for each available language plus
  // x-default (→ primary). The static SPA shell ships a single lang, so we
  // inject these client-side for crawlers that execute JS / for completeness.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const MARK = "data-wb-hreflang";
    const head = document.head;
    head.querySelectorAll(`link[${MARK}]`).forEach((el) => el.remove());
    if (availableLanguages.length <= 1) return;

    const make = (hreflang: string, lang: string): void => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute(MARK, "");
      const url = new URL(window.location.href);
      url.searchParams.set("lang", lang);
      link.setAttribute("href", url.toString());
      head.appendChild(link);
    };
    availableLanguages.forEach((lang) => make(lang, lang));
    make("x-default", primary);

    return () => {
      head.querySelectorAll(`link[${MARK}]`).forEach((el) => el.remove());
    };
  }, [availableLanguages, primary]);

  const setCurrentLang = (lang: string): void => {
    const resolved = resolveLangKey(availableLanguages, lang, primary);
    setCurrentLangState(resolved);
    try {
      window.localStorage.setItem(STORAGE_KEY, resolved);
    } catch {
      /* ignore storage failures (private mode) */
    }
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", resolved);
      window.history.replaceState(null, "", url.toString());
    }
  };

  const value = useMemo(
    () => ({ currentLang, primaryLanguage: primary, availableLanguages, setCurrentLang }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLang, primary, availableLanguages],
  );

  return <WBLangContext.Provider value={value}>{children}</WBLangContext.Provider>;
};

/**
 * Read the active content language. Never throws: defaults to "de-DE" if no
 * provider is mounted (defensive — the provider always wraps the Frame).
 */
export function useWBLang(): {
  currentLang: string;
  primaryLanguage: string;
  availableLanguages: string[];
  setCurrentLang: (lang: string) => void;
} {
  const ctx = useContext(WBLangContext);
  if (!ctx) {
    return {
      currentLang: "de-DE",
      primaryLanguage: "de-DE",
      availableLanguages: ["de-DE"],
      setCurrentLang: () => {},
    };
  }
  return ctx;
}
